import * as styles from "./DrawerPopup.css";

import { clsx } from "clsx";
import {
  Children,
  type JSX,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
} from "react";

import { Drawer as BaseDrawer } from "@base-ui/react/drawer";
import {
  ShortcutScopeContext,
  useShortcutScope,
} from "@frontend/hooks/useScopedShortcut";
import { resolveSize } from "@frontend/styles";
import { SIZES } from "@frontend/styles/constants";

import type { DrawerPopupProps } from "./Drawer.types";
import { DrawerBackdrop } from "./DrawerBackdrop";
import { DrawerGrabber } from "./DrawerGrabber";
import { DrawerPortal } from "./DrawerPortal";
import { DrawerViewport } from "./DrawerViewport";

type ClassNamed = { className?: string };

function extractGrabbers(children: ReactNode): [ReactNode[], ReactNode[]] {
  const grabbers: ReactNode[] = [];
  const rest: ReactNode[] = [];

  for (const child of Children.toArray(children)) {
    if (isValidElement(child) && child.type === DrawerGrabber) {
      const element = child as ReactElement<ClassNamed>;

      grabbers.push(
        cloneElement(element, {
          className: clsx(element.props.className, styles.ringGrabber),
        }),
      );
    } else {
      rest.push(child);
    }
  }

  return [grabbers, rest];
}

export function DrawerPopup({
  className,
  children,
  size = "md",
  border = "solid",
  backdrop = true,
  container,
  style,
  ...props
}: DrawerPopupProps): JSX.Element {
  const { variant: sizeVariant, style: sizeVar } = resolveSize(
    size,
    SIZES,
    styles.customSize,
  );

  const popupStyle = !sizeVar
    ? style
    : typeof style === "function"
      ? (state: Parameters<typeof style>[0]) => ({
          ...sizeVar,
          ...style(state),
        })
      : { ...sizeVar, ...style };

  const { scope, ref } = useShortcutScope({ modal: backdrop });

  let body = children;
  let hasRingGrabber = false;

  if (border !== "solid") {
    const [grabbers, rest] = extractGrabbers(children);

    hasRingGrabber = grabbers.length > 0;
    body = (
      <>
        {grabbers}
        <div data-slot="drawer-panel" className={styles.panel}>
          {rest}
        </div>
      </>
    );
  }

  return (
    <DrawerPortal container={container}>
      {backdrop ? <DrawerBackdrop /> : null}
      <DrawerViewport>
        <BaseDrawer.Popup
          ref={ref}
          data-slot="drawer-popup"
          className={clsx(
            styles.popup({ size: sizeVariant, border }),
            hasRingGrabber && styles.ringGrabberBand,
            className,
          )}
          style={popupStyle}
          {...props}
        >
          <ShortcutScopeContext value={scope}>{body}</ShortcutScopeContext>
        </BaseDrawer.Popup>
      </DrawerViewport>
    </DrawerPortal>
  );
}
